import type { Country, Language } from "@/data/types";
import type { EnhancedSelectOption } from "@/components/ui/enhanced-multiselect";

// Import the JSON data
import countriesData from "@/data/countries.json";
import countriesSuggestedData from "@/data/countries-suggested.json";
import languagesData from "@/data/languages.json";
import languagesSuggestedData from "@/data/languages-suggested.json";

// Helper function to create options from countries
export const createCountryOptions = (): EnhancedSelectOption[] => {
  const suggestedCodes = new Set(
    (countriesSuggestedData as Country[]).map(country => country.code)
  );

  const suggested = (countriesSuggestedData as Country[]).map(country => ({
    value: country.code,
    label: country.name,
    emoji: country.emoji,
    suggested: true,
  }));

  const regular = (countriesData as Country[])
    .filter(country => !suggestedCodes.has(country.code))
    .map(country => ({
      value: country.code,
      label: country.name,
      emoji: country.emoji,
      suggested: false,
    }));

  return [...suggested, ...regular];
};

// Helper function to create options from languages
export const createLanguageOptions = (): EnhancedSelectOption[] => {
  const suggestedCodes = new Set(
    (languagesSuggestedData as Language[]).map(language => language.code)
  );

  const suggested = (languagesSuggestedData as Language[]).map(language => ({
    value: language.code,
    label: language.name,
    emoji: language.emoji,
    suggested: true,
  }));

  const regular = (languagesData as Language[])
    .filter(language => !suggestedCodes.has(language.code))
    .map(language => ({
      value: language.code,
      label: language.name,
      emoji: language.emoji,
      suggested: false,
    }));

  return [...suggested, ...regular];
};

// Helper function to create phone country code options
export const createPhoneCountryCodeOptions = (): EnhancedSelectOption[] => {
  const suggestedCodes = new Set(
    (countriesSuggestedData as Country[]).map(country => country.code)
  );

  const suggested = (countriesSuggestedData as Country[])
    .filter(country => country.dial)
    .map(country => ({
      value: country.code,
      label: `${country.emoji} ${country.dial}`,
      emoji: country.emoji,
      suggested: true,
    }));

  const regular = (countriesData as Country[])
    .filter(country => !suggestedCodes.has(country.code) && country.dial)
    .map(country => ({
      value: country.code,
      label: `${country.emoji} ${country.dial}`,
      emoji: country.emoji,
      suggested: false,
    }));

  return [...suggested, ...regular];
};

// Get dial code by country ISO code
export const getDialCodeByCountryCode = (code: string | null | undefined): string | undefined => {
  if (!code || typeof code !== 'string' || code.trim() === '') {
    return undefined;
  }
  const country = (countriesData as Country[]).find(c => c.code === code.trim());
  return country?.dial || undefined;
};

// Countries where the leading "0" is part of the subscriber number
const KEEP_LEADING_ZERO_CODES = new Set(['IT', 'SM', 'VA']);

// Format a phone number with its international dial code prefix.
// Strips the trunk prefix "0" except for Italy/San Marino/Vatican City.
export const formatPhoneWithCountryCode = (
  phone: string | null | undefined,
  countryCode: string | null | undefined,
): string | undefined => {
  if (!phone) return undefined;
  if (!countryCode) return phone;
  const dial = getDialCodeByCountryCode(countryCode);
  if (!dial) return phone;
  const strippedPhone = KEEP_LEADING_ZERO_CODES.has(countryCode.trim().toUpperCase())
    ? phone
    : phone.replace(/^0/, '');
  return `${dial} ${strippedPhone}`;
};

// Get country name by code
export const getCountryByCode = (code: string | null | undefined): Country | undefined => {
  if (!code || typeof code !== 'string' || code.trim() === '') {
    return undefined;
  }
  return (countriesData as Country[]).find(country => country.code === code.trim());
};

// Get language name by code
export const getLanguageByCode = (code: string | null | undefined): Language | undefined => {
  if (!code || typeof code !== 'string' || code.trim() === '') {
    return undefined;
  }
  return (languagesData as Language[]).find(language => language.code === code.trim());
}; 